<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div :class="sectionClass">
    <div
      class="multiselect-wrapper relative"
      :class="{ 'dropdown-active': showDropdown && !disabled }"
    >
      <div
        class="custom-multiselect w-full"
        :class="{
          'p-invalid': validation?.$error,
          disabled: disabled,
          'dropdown-active': showDropdown && !disabled,
        }"
        @click="toggleDropdown"
        @keydown="handleKeydown"
        :aria-describedby="`${id}-help`"
        tabindex="0"
        ref="multiselect"
      >
        <div
          class="selected-items flex items-center w-full text-sm text-gray-900 gap-2"
        >
          <span v-if="!selectedItems.length && placeholder" class="placeholder">
            {{ placeholder }}
          </span>
          <span v-else class="selected-text">
            {{ selectedItemsLabel }}
          </span>
        </div>
        <img :src="iconSvg" alt="Dropdown icon" class="iconSvg h-4 ml-auto" />
      </div>

      <div
        v-if="showDropdown"
        class="dropdown-menu absolute w-full bg-white border border-gray-300 max-h-60 overflow-y-auto"
      >
        <!-- Search -->
        <div
          v-if="searchable"
          class="search-wrapper p-2 border-b border-gray-200 flex items-center"
        >
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search..."
            class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-500 text-sm"
            @keydown="handleKeydown"
            ref="searchInput"
          />
        </div>

        <!-- Options -->
        <div
          v-for="(option, index) in filteredOptions"
          :key="index"
          class="dropdown-item flex items-center cursor-pointer"
          :class="{
            'disabled-option': optionDisabled && optionDisabled(option),
            'selected-item-highlight': isSelected(option),
            'hovered-item': index === hoveredIndex,
          }"
          @click="toggleOption(option)"
          @mouseenter="hoveredIndex = index"
        >
          <input
            type="checkbox"
            :checked="isSelected(option)"
            :disabled="optionDisabled && optionDisabled(option)"
            class="custom-checkbox"
          />
          {{ optionLabel ? option[optionLabel] : option }}
        </div>
      </div>
    </div>

    <!-- Validation Messages -->
    <span
      v-if="validation?.$error && validation?.required?.$invalid"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ PropName }} is required.
    </span>
    <span
      v-else-if="validation?.$error && validation?.requiredIf?.$invalid"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ validation.requiredIf.$message.replace("value", PropName) }}
    </span>
  </div>
</template>

<script>
import iconSvg from "@/assets/icons/icon-down.svg"; // Adjust the path and file name as needed

export default {
  props: {
    sectionClass: { type: String },
    id: { type: String, required: true },
    validation: Object,
    options: { type: Array, default: () => [] },
    modelValue: {
      type: [Array, String], // Allow String to handle invalid cases
      default: () => [],
    },
    optionLabel: { type: String, default: "" },
    optionValue: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    optionDisabled: { type: Function, default: null },
    PropName: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    maxSelectedLabels: { type: Number, default: 2 },
    searchable: { type: Boolean, default: false },
  },
  data() {
    return {
      showDropdown: false,
      searchQuery: "",
      iconSvg,
      hoveredIndex: 0, // Track the hovered (highlighted) option
    };
  },
  computed: {
    selectedItems() {
      // Normalize modelValue to an array if it's a string
      const normalizedModelValue = Array.isArray(this.modelValue)
        ? this.modelValue
        : [];

      if (this.optionValue) {
        // Map normalizedModelValue to options to maintain selection order
        return normalizedModelValue
          .map((value) =>
            this.options.find((option) => option[this.optionValue] === value)
          )
          .filter((item) => item !== undefined); // Filter out undefined values
      }
      return normalizedModelValue;
    },
    selectedItemsLabel() {
      const labels = this.selectedItems.map((item) =>
        this.optionLabel ? item[this.optionLabel] : item
      );
      return labels.join(", "); // Join with comma and space
    },
    filteredOptions() {
      if (!this.searchable || !this.searchQuery) {
        return this.options;
      }
      return this.options.filter((option) => {
        const label = this.optionLabel ? option[this.optionLabel] : option;
        return label.toLowerCase().includes(this.searchQuery.toLowerCase());
      });
    },
    displayedItems() {
      return this.selectedItems.slice(0, this.maxSelectedLabels);
    },
  },
  methods: {
    toggleDropdown() {
      if (!this.disabled) {
        this.showDropdown = !this.showDropdown;
        if (this.showDropdown) {
          this.hoveredIndex = 0; // Set first option as hovered when dropdown opens
          this.$nextTick(() => {
            if (this.searchable && this.$refs.searchInput) {
              this.$refs.searchInput.focus(); // Focus search input if searchable
            } else {
              this.$refs.multiselect.focus(); // Focus multiselect for key navigation
            }
          });
        }
      }
    },
    toggleOption(option) {
      if (this.optionDisabled && this.optionDisabled(option)) return;
      const value = this.optionValue ? option[this.optionValue] : option;
      // Normalize modelValue to an array
      let newValue = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
      if (newValue.includes(value)) {
        newValue = newValue.filter((item) => item !== value);
      } else {
        newValue.push(value);
      }
      this.$emit("update:modelValue", newValue);
    },
    isSelected(option) {
      const value = this.optionValue ? option[this.optionValue] : option;
      // Normalize modelValue for checking
      const normalizedModelValue = Array.isArray(this.modelValue)
        ? this.modelValue
        : [];
      return normalizedModelValue.includes(value);
    },
    closeDropdown(event) {
      if (!this.$el.contains(event.target)) {
        this.showDropdown = false;
        this.hoveredIndex = 0; // Reset hovered index when closing
      }
    },
    handleKeydown(event) {
      if (!this.showDropdown) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          if (this.hoveredIndex < this.filteredOptions.length - 1) {
            this.hoveredIndex++;
            this.scrollToHoveredOption();
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (this.hoveredIndex > 0) {
            this.hoveredIndex--;
            this.scrollToHoveredOption();
          }
          break;
        case "Enter":
          event.preventDefault();
          if (this.filteredOptions[this.hoveredIndex]) {
            this.toggleOption(this.filteredOptions[this.hoveredIndex]);
          }
          break;
        case "Escape":
          this.showDropdown = false;
          this.hoveredIndex = 0;
          break;
      }
    },
    scrollToHoveredOption() {
      this.$nextTick(() => {
        const dropdown = this.$el.querySelector(".dropdown-menu");
        const hoveredElement =
          this.$el.querySelectorAll(".dropdown-item")[this.hoveredIndex];
        if (dropdown && hoveredElement) {
          const dropdownRect = dropdown.getBoundingClientRect();
          const hoveredRect = hoveredElement.getBoundingClientRect();
          const isVisible =
            hoveredRect.top >= dropdownRect.top &&
            hoveredRect.bottom <= dropdownRect.bottom;

          if (!isVisible) {
            hoveredElement.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }
        }
      });
    },
  },
  mounted() {
    document.addEventListener("click", this.closeDropdown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeDropdown);
  },
};
</script>

<style scoped lang="scss">
.custom-multiselect {
  display: flex;
  align-items: center;
  border: 1px solid #c0c0bc;
  border-radius: 0.25rem;
  height: 40px;
  width: 395px;
  font-size: 16px;
  justify-content: space-between;
  padding-right: 0.65rem;
  padding-left: 0.35rem;
  background: white;
  cursor: pointer;
  padding: 4px 12px;
  outline: none;
}

.custom-multiselect.disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.multiselect-wrapper{
  border-radius: 0.25rem;
  outline: none;
}

.multiselect-wrapper:focus,
.multiselect-wrapper:active,
.multiselect-wrapper:focus-within {
  box-shadow: 0px 0px 0px 2px #ffffff, 0px 0px 0px 4px #21211f;
}

.selected-items {
  // padding: 8px 0;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  align-items: center;
  border: none;
  outline: none;
}

.selected-text {
  color: #21211f;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 14.22px;
}

.placeholder {
  color: #6c757d !important;
  font-size: 16px !important;
}

.dropdown-menu {
  z-index: 1000;
  border-radius: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  max-height: 240px;
  margin-top: 10px;
  outline: none;
}

.dropdown-item {
  height: 44px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  padding: 12px 20px;
  font-size: 14.22px;
  color: #212529;
}

.dropdown-item:hover,
.hovered-item {
  background: #e9ecef;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.selected-item-highlight {
  font-weight: 500;
  color: #212529;
  background: #f0f0ef !important;
}

.selected-item-highlight.hovered-item {
  background: #e9ecef !important;
}

.group-header {
  background-color: #f9fafb;
  font-size: 14px;
}

.custom-checkbox {
  accent-color: #43a584;
  width: 16px;
  height: 16px;
  margin-left: 0;
  margin-right: 8px;
}

.disabled-option {
  background: #f5f5f5;
  color: #6b7280;
  cursor: not-allowed;
}

.p-invalid {
  border-color: #dc2626;
}

.p-error {
  color: #dc2626;
  font-size: 0.875rem;
}

::deep(.text-sm) {
  font-size: 14.22px !important;
}
</style>
